import { Position, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTrashNode } from './commons/trashNode';
import { FlowHandle } from './commons/flowHandle';
import './style.scss';
import { UilTrashAlt } from '@iconscout/react-unicons';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import { useEdges } from '@xyflow/react';
import { Button } from 'react-bootstrap';
import { handleClipboardKeyDown } from '../helper';
import {
  ensureChatBotCompanyKey,
  selectChatBot
} from 'store/slices/chatbot/chatbotSlice';
import { getCalendars } from 'store/slices/appointment/appointmentSlice';

const DEFAULT_OUTCOMES = [
  { value: 'Booked', outcome: 'booked' },
  { value: 'Declined', outcome: 'declined' },
  { value: 'Unavailable', outcome: 'unavailable' },
  { value: 'Fallback', outcome: 'fallback' }
];

export default function AppointmentNode({ id, data, editable = true }) {
  const { setNodes } = useReactFlow();
  const trashNode = useTrashNode();
  const edges = useEdges();
  const updateNodeInternals = useUpdateNodeInternals();
  const dispatch = useDispatch();
  const chatbot = useSelector(selectChatBot);
  const companyKey = chatbot?.companyKey || chatbot?.CompanyKey || '';

  useEffect(() => {
    if (chatbot && !(chatbot.companyKey || chatbot.CompanyKey)) {
      dispatch(ensureChatBotCompanyKey());
    }
  }, [chatbot?.id, chatbot?.companyId, chatbot?.CompanyId, dispatch]);

  const defaultTransitions = () =>
    DEFAULT_OUTCOMES.map((item, index) => ({
      id: `transition-${id}-${index}`,
      value: item.value,
      outcome: item.outcome
    }));

  const ensureTransitions = list => {
    const defaults = defaultTransitions();
    if (!Array.isArray(list) || list.length < 4) return defaults;
    return list.slice(0, 4).map((transition, index) => ({
      ...defaults[index],
      ...transition,
      id: transition.id || defaults[index].id,
      value: transition.value || defaults[index].value,
      outcome: transition.outcome || defaults[index].outcome
    }));
  };

  const [items, setItems] = useState(() =>
    ensureTransitions(data?.transitions)
  );
  const [text, setText] = useState(data?.text ?? '');
  const [calendarId, setCalendarId] = useState(data?.calendarId ?? '');
  const [serviceTypes, setServiceTypes] = useState([]);
  const [requireConsent, setRequireConsent] = useState(
    data?.requireConsent !== false
  );

  const onChange = newData => {
    setNodes(nds =>
      nds.map(node =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  const IsValidConnection = connection => {
    if (edges.some(edge => edge.sourceHandle === connection.sourceHandle))
      return false;
    return true;
  };

  useEffect(() => {
    updateNodeInternals(id);
  }, []);

  useEffect(() => {
    if (!companyKey) {
      setServiceTypes([]);
      return;
    }

    let cancelled = false;
    getCalendars({ company_key: companyKey })
      .then(response => {
        if (cancelled) return;
        setServiceTypes(response?.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setServiceTypes([]);
      });

    return () => {
      cancelled = true;
    };
  }, [companyKey]);

  useEffect(() => {
    const next = ensureTransitions(data?.transitions);
    const needsTransitions =
      !data?.transitions ||
      data.transitions.length < 4 ||
      data.transitions.some(t => !t?.outcome || !t?.id);
    const patch = {};

    if (needsTransitions) {
      setItems(next);
      patch.transitions = next;
    }
    if (data?.requireConsent === undefined) {
      patch.requireConsent = true;
    }

    if (Object.keys(patch).length > 0) {
      onChange(patch);
      updateNodeInternals(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSelectedInList =
    !calendarId ||
    serviceTypes.some(
      calendar => String(calendar.CalendarId) === String(calendarId)
    );

  return (
    <dl className="mt-3" style={{ width: '320px' }}>
      <FlowHandle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        editable={editable}
        isConnectable={true}
      />
      <div
        className="card shadow-sm border-gray rounded-10"
        style={{ backgroundColor: '#F9F9F9' }}
      >
        <div
          className="d-flex align-items-center px-4 node-header"
          style={{ backgroundColor: '#DFF6F0' }}
        >
          <div className="d-flex mr-auto ml-2">
            <dt
              className="text-truncate fw-semibold fs-6"
              style={{ color: '#0F6B5C' }}
            >
              Appointment Node
            </dt>
          </div>
          <div className="ms-auto">
            <Button
              variant="transparent"
              onClick={e => trashNode(e, id)}
              className="d-flex align-items-center rounded-circle p-2 header-delete-icon"
              title="Delete appointment"
              disabled={!editable}
            >
              <UilTrashAlt
                style={{ width: '24px', height: '24px', color: '#dc3545' }}
              />
            </Button>
          </div>
        </div>

        <div className="card-body p-2">
          <div className="px-2 mb-3">
            <div className="small text-muted mb-1">Intro text</div>
            <SimpleLabelTextBox
              dataProperty={text}
              maxRows={6}
              minRows={2}
              multiline
              editable={editable}
              id={`text-${id}`}
              handleChange={e => {
                setText(e.target.value);
                onChange({ text: e.target.value });
              }}
              onkeyPress={handleClipboardKeyDown}
              inputClasses="custom-scroll"
              placeholder="I can help schedule an appointment. Would you like me to book one for you?"
              inputStyles={{ backgroundColor: 'white', padding: '10px' }}
              inputPropClasses="custom-placeholder"
            />
          </div>

          <div className="px-2 mb-3">
            <div className="small text-muted mb-1">Service Type</div>
            <select
              className="form-select form-select-sm"
              value={calendarId}
              disabled={!editable}
              onChange={e => {
                const nextId = e.target.value;
                setCalendarId(nextId);
                onChange({ calendarId: nextId });
              }}
            >
              <option value="">Select service type</option>
              {!hasSelectedInList && (
                <option value={calendarId}>{calendarId}</option>
              )}
              {serviceTypes.map(calendar => (
                <option key={calendar.CalendarId} value={calendar.CalendarId}>
                  {calendar.Name || calendar.Purpose || calendar.CalendarId}
                </option>
              ))}
            </select>
          </div>

          <div className="form-check px-3 mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id={`requireConsent-${id}`}
              checked={requireConsent}
              disabled={!editable}
              onChange={e => {
                setRequireConsent(e.target.checked);
                onChange({ requireConsent: e.target.checked });
              }}
            />
            <label
              className="form-check-label small"
              htmlFor={`requireConsent-${id}`}
            >
              Require consent
            </label>
          </div>

          <div className="px-2 pb-3">
            <dt
              className="text-truncate small fw-medium mt-1 px-2"
              style={{ color: '#6B7280', fontSize: '16px' }}
            >
              Transitions
            </dt>
            {items.map(item => (
              <div
                key={item.id}
                className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
              >
                <FlowHandle
                  type="source"
                  position={Position.Right}
                  id={item.id}
                  style={{
                    right: '-13px',
                    top: '50%'
                  }}
                  editable={false}
                  isConnectable={true}
                  isValidConnection={IsValidConnection}
                />
                <SimpleLabelTextBox
                  dataProperty={item.value}
                  editable={false}
                  inputStyles={{
                    backgroundColor: '#f1f3f5',
                    cursor: 'not-allowed'
                  }}
                  inputPropStyle={{ padding: '10px' }}
                  styles={{ flex: 1 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </dl>
  );
}
